<template>
  <div class="wrapper" ref="wrapperRef">
    <div class="input-row">
      <VTextField 
        ref="fieldInputEl"
        :readOnly="readOnly"
        v-model="computedValue"
        :placeholder="placeholder"
        appearance="ghost"
        class="vivid-input"
        @blur="$emit('inputChange', computedValue);"
      ></VTextField>
      <div @click.stop="() => {isBotBuilder ? (onlyBotVariables ? addBotVariableMode = true : showOptionsDropDown = true) : addCustomFieldMode = true}" v-if="!readOnly" class="icon-btn">
        <VIcon :name="codeWorkingOutline"></VIcon>
      </div>
    </div>
    <!--
      The options dropdown is promoted into the browser's native top layer via
      <top-layer> instead of being teleported to <body>. Teleporting moved it
      *out* of an enclosing Vivid modal dialog, and a modal dialog both paints
      above all normal-flow content (it lives in the top layer) and marks
      everything outside itself `inert` — so the teleported dropdown ended up
      hidden behind the dialog and unclickable. Keeping it in place and showing
      it as a popover puts it in the top layer (so it paints above the dialog,
      because top-layer paint order follows entry order) while it stays a DOM
      descendant of the dialog (so it is not inert and remains interactive).
    -->
    <top-layer :visible="showOptionsDropDown">
      <div v-if="showOptionsDropDown" class="dropdown-options" :style="dropdownStyle">
        <div class="upload-row" @click.stop="() => { addBotVariableMode = true; showOptionsDropDown = false; }">
          <div class="icon">
            <VIcon :name="codeWorkingOutline"></VIcon>
          </div>
          <div class="label">Bot Variable</div>
        </div>
        <div class="upload-row" @click.stop="() => { addCustomFieldMode = true; showOptionsDropDown = false; }">
          <div class="icon">
            <VIcon :name="codeWorkingOutline"></VIcon>
          </div>
          <div class="label">Custom Field Variable</div>
        </div>
      </div>
    </top-layer>
    <CustomDataFieldPickerModal :canAddNew="!$route.path.includes('page-inbox')" v-model:isOpen="addCustomFieldMode" @selected="(v) => { appendValue(v); addCustomFieldMode = false }" />
    <BotVariablePickerModal v-if="$route?.params?.id" :bot_id="$route?.params?.id" v-model:isOpen="addBotVariableMode" @selected="(v) => { appendValue(v); addBotVariableMode = false; }" :formattedOutput="true"></BotVariablePickerModal>
  </div>
</template>

<script>
import CustomDataFieldPickerModal from '@/components/CustomDataFieldPickerModal.vue';
import BotVariablePickerModal from '@/components/BotVariablePickerModal.vue';
import TopLayer from '@/components/TopLayer.vue';

export default {
  components: {
    CustomDataFieldPickerModal,
    BotVariablePickerModal,
    TopLayer
  },
  emits: ['update:modelValue', 'inputChange'],
  props: {
    modelValue: {
      type: String
    },
    placeholder: {
      type: String,
      default: "Enter here..."
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    onlyBotVariables: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      codeWorkingOutline: 'curly-brackets-line',
      showOptionsDropDown: false,
      addCustomFieldMode: false,
      addBotVariableMode: false,
      dropdownStyle: {}
    }
  },
  watch: {
    showOptionsDropDown(isOpen) {
      if (isOpen) {
        this.updateDropdownPosition();
        window.addEventListener('scroll', this.updateDropdownPosition, true);
        window.addEventListener('resize', this.updateDropdownPosition);
      } else {
        window.removeEventListener('scroll', this.updateDropdownPosition, true);
        window.removeEventListener('resize', this.updateDropdownPosition);
      }
    }
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.updateDropdownPosition, true);
    window.removeEventListener('resize', this.updateDropdownPosition);
  },
  methods: {
    updateDropdownPosition() {
      const rect = this.$refs.wrapperRef?.getBoundingClientRect();
      if (!rect) return;
      this.dropdownStyle = {
        position: 'fixed',
        top: `${rect.bottom + 10}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`
      };
    },
    appendValue(value) {
      const currentValue = this.$refs.fieldInputEl?.$el?.querySelector('input')?.value
        ?? this.$refs.fieldInputEl?.value
        ?? this.computedValue;
      this.computedValue = currentValue ? currentValue + value : value;
      this.$emit('inputChange', this.computedValue);
    }
  },
  computed: {
    isBotBuilder() {
      return this.$route?.path?.includes('/bot-builder/create/') && this.$route?.params?.id;
    },
    computedValue: {
      get() {
        return this.modelValue;
      },
      set(nVal) {
        this.$emit('update:modelValue', nVal);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.wrapper {
  width: 100%;
  position: relative;
}
.input-row {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--background-primary);
  border-radius: 5px;
  .vivid-input {
    flex: 1;
  }
  .icon-btn {
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    &:hover {
      color: var(--purple);
    }
  }
}
.dropdown-options {
  border: 1px solid var(--gray-01);
  border-radius: 5px;
  box-shadow: 2px 2px 4px 2px rgba(0, 0, 0, 0.2);
  background-color: var(--white);
  z-index: 9999;
  /* The <top-layer> host is pointer-events: none so it never becomes a
     full-viewport click blocker; the dropdown opts back in. */
  pointer-events: auto;
  .upload-row {
    cursor: pointer;
    &:not(:last-child) {
      border-bottom: 1px solid var(--gray-01);
    }
    &:hover {
      background-color: var(--gray-02);
    }
  }
}
.upload-row {
  display: flex;
  flex-direction: row;
  align-content: center;
  align-items: center;
  gap: 10px;
  width: 100%;
  .icon {
    height: 40px;
    width: 40px;
    display: flex;
    align-content: center;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
}
.offlineModalWrapper {
  --width: 430px !important;
  --height: 180px;
  --border-radius: 8px;
  z-index: 99999 !important;
  .offlineModal {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    align-content: center;
    justify-content: center;
    padding: 20px;
    border-radius: 5px;
    background-color: var(--white);
    text-align: center;
    .heading {
      font-size: 1.2rem;
      font-weight: bold;
      color: var(--black);
      margin-bottom: 20px;
    }
    .sub-text {
      font-size: 0.9rem;
      color: var(--gray-04);
    }
  }
  .customFooter {
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    gap: 10px;
    padding: 10px;
    ion-button {
      cursor: pointer;
    }
  }
}
.rtb-variable-list__item {
  padding: 10px 16px;
  font-size: 0.95rem;
  cursor: pointer;
  border-bottom: 1px solid var(--background-primary);
  transition: background 0.1s ease;

  &:last-child {
    border-bottom: none;
  }
}
</style>